import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import styled from '@emotion/styled';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import {
  PlannerStore,
  DutyExchangeStore,
  DutyAssignmentStore,
  SystemStatusStore,
  SecretariatStore,
  AuthenticationStore,
} from '../../store';
import { SystemStatus } from '../../store/models/SystemStatus';
import { Button, ButtonGroup, InputGroup, Intent, NumericInput, Spinner } from '@blueprintjs/core';
import { getFirstMonday, getFirstSunday } from '../../common/utils';
import moment from 'moment';

interface DutyDownloadTabProps extends WithTranslateProps {
  secretariatId?: number;
  refresh: () => void;
  PlannerStore?: Instance<typeof PlannerStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  DutyExchangeStore?: Instance<typeof DutyExchangeStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface DutyDownloadTabState {
  fileIsLoading?: boolean;
  isDownloading?: boolean;
  modalFileLoadingOpened: boolean;
  dutiesImportError: string[];
  year: number;
  startDatePlanning?: Date | undefined;
  endDatePlanning?: Date | undefined;
  firstSemesterDate?: string | undefined;
  lastSemesterDate?: string | undefined;
  selectedSemester: string;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  fileLinesLimit: number;
  exchangeExportIsEmpty?: boolean;
  assignmentExportIsEmpty?: boolean;
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

// TODO : Delete this. This is used nowhere.
@inject('PlannerStore')
@inject('DutyExchangeStore', 'DutyAssignmentStore')
@inject('SystemStatusStore', 'AuthenticationStore', 'SecretariatStore')
@observer
class DutyDownloadTab extends React.Component<DutyDownloadTabProps, DutyDownloadTabState> {
  state = {
    fileIsLoading: false,
    isDownloading: false,
    modalFileLoadingOpened: false,
    dutiesImportError: [],
    year: new Date().getFullYear(),
    selectedSemester: '',
    startDatePlanning: undefined,
    endDatePlanning: undefined,
    fileLinesLimit: 2000,
    exchangeExportIsEmpty: false,
    assignmentExportIsEmpty: false,
  } as DutyDownloadTabState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const criteria: any = {
      subSystem: 'MaxExchangeItemIdSentToWebGarde',
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    // @ts-ignore
    this.setState({ systemStatus });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  cleanupBuffer = (buffer) => {
    console.log(buffer.length);
    return buffer.substring(39, 100).replace(/\//g, '-');
  };

  onImportModalClose = () => {
    this.setState({
      modalFileLoadingOpened: false,
      fileIsLoading: false,
    });
  };

  getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId;
    return undefined;
  };

  downloadDutyAssignments = async (data) => {
    this._isMounted && this.setState({ isDownloading: true });
    console.log(this.state.startDatePlanning);
    console.log(this.state.endDatePlanning);
    await this.props.PlannerStore!.downloadPlanning({
      from: this.state.firstSemesterDate!,
      to: this.state.lastSemesterDate!,
      fileLinesLimit: this.state.fileLinesLimit,
      secretariatId: this.getSecretariatId(),
    });
    this._isMounted && this.setState({ isDownloading: false });
  };

  downloadDutyExchanges = async (data) => {
    this._isMounted && this.setState({ isDownloading: true });
    const exportIsEmpty = await this.props.DutyExchangeStore!.downloadLatestExchanges();
    this._isMounted && this.setState({ isDownloading: false, exchangeExportIsEmpty: exportIsEmpty });
  };

  downloadDutyAdditions = async () => {
    this._isMounted && this.setState({ isDownloading: true });
    const exportIsEmpty = await this.props.DutyAssignmentStore!.downloadLatestAdditions();
    this._isMounted && this.setState({ assignmentExportIsEmpty: exportIsEmpty! });
    this._isMounted && this.setState({ isDownloading: false });
  };

  onYearChange = (valueAsNumber: number, valueAsString: string) => {
    this.setState({ year: valueAsNumber });
    if (this.state.selectedSemester === 'S1') this.setS1();
    if (this.state.selectedSemester === 'S2') this.setS2();
  };

  setS1 = () => {
    let date = moment();
    date.set('year', this.state.year);
    date.set('month', 0);
    date.set('date', 1);
    this.setState({ startDatePlanning: date.toDate() });
    date.set('month', 6);
    date.set('date', 0);
    this.setState({ endDatePlanning: date.toDate() });
    const firstDate = getFirstMonday(this.state.year, 0);
    this.setState({ firstSemesterDate: moment(firstDate).format('YYYY-MM-DD') });
    const lastDate = getFirstSunday(this.state.year, 6);
    this.setState({ lastSemesterDate: moment(lastDate).format('YYYY-MM-DD') });
    this.setState({ selectedSemester: 'S1' });
  };

  setS2 = () => {
    let date = moment();
    date.set('year', this.state.year);
    date.set('month', 6);
    date.set('date', 1);
    this.setState({ startDatePlanning: date.toDate() });
    date.set('month', 12);
    date.set('date', 0);
    this.setState({ endDatePlanning: date.toDate() });
    const firstDate = getFirstMonday(this.state.year, 6);
    this.setState({ firstSemesterDate: moment(firstDate).format('YYYY-MM-DD') });
    const lastDate = getFirstSunday(this.state.year + 1, 0);
    this.setState({ lastSemesterDate: moment(lastDate).format('YYYY-MM-DD') });
    this.setState({ selectedSemester: 'S2' });
  };

  onFileLinesLimitChange = (valueAsNumber: number, valueAsString: string) => {
    this.setState({ fileLinesLimit: valueAsNumber });
  };

  render() {
    const { isDownloading } = this.state;
    const { translate } = this.props;
    return (
      <Container>
        &nbsp;
        {isDownloading ? (
          <>
            <SpinnerContainer>
              <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
            </SpinnerContainer>
          </>
        ) : (
          <>
            <DivLine>
              <TranslatedMessage id={TLCode.ROSTER_DownloadDutyAssignments} />
              &nbsp;
              <NumericInput min={2016} value={this.state.year} onValueChange={this.onYearChange} />
              &nbsp;
              <ButtonGroup>
                <Button intent="primary" onClick={this.setS1}>
                  <TranslatedMessage id={TLCode.ROSTER_S1} />
                </Button>
                &nbsp;
                <Button intent="primary" onClick={this.setS2}>
                  <TranslatedMessage id={TLCode.ROSTER_S2} />
                </Button>
              </ButtonGroup>
              &nbsp;
              {this.state.selectedSemester === '' ? (
                `${translate(TLCode.ROSTER_SemesterNotSelected)}`
              ) : (
                <>
                  <TranslatedMessage id={TLCode.ROSTER_S1} />
                  &nbsp;
                  <InputGroup value={this.state.firstSemesterDate} readOnly />
                  &nbsp;
                  <TranslatedMessage id={TLCode.ROSTER_S2} />
                  &nbsp;
                  <InputGroup value={this.state.lastSemesterDate} readOnly />
                </>
              )}
              &nbsp;&nbsp;
              <TranslatedMessage id={TLCode.ROSTER_FileLinesLimit} />
              &nbsp;{' '}
              <NumericInput min={2} value={this.state.fileLinesLimit} onValueChange={this.onFileLinesLimitChange} />
              &nbsp;
              <ButtonGroup>
                <Button
                  intent="primary"
                  onClick={this.downloadDutyAssignments}
                  disabled={this.state.selectedSemester === ''}
                >
                  <TranslatedMessage id={TLCode.ROSTER_DownloadDutyAssignmentsButton} />
                </Button>
              </ButtonGroup>
            </DivLine>
            <br />
            <DivLine>
              <TranslatedMessage id={TLCode.ROSTER_DownloadDutyExchanges} />
              &nbsp;
              <ButtonGroup>
                <Button intent="primary" onClick={this.downloadDutyExchanges}>
                  <TranslatedMessage id={TLCode.ROSTER_DownloadDutyExchangesButton} />
                </Button>
                {this.state.exchangeExportIsEmpty && (
                  <div
                    style={{
                      backgroundColor: 'red',
                      color: 'white',
                      fontWeight: 'bold',
                      marginLeft: '10px',
                      marginRight: '10px',
                    }}
                  >
                    &nbsp;Pas de données à exporter&nbsp;
                  </div>
                )}
                <Button intent="primary" onClick={this.downloadDutyAdditions}>
                  <TranslatedMessage id={TLCode.ROSTER_DownloadAddedDutyAssignmentsButton} />
                </Button>
                {this.state.assignmentExportIsEmpty && (
                  <div
                    style={{
                      backgroundColor: 'red',
                      color: 'white',
                      fontWeight: 'bold',
                      marginLeft: '10px',
                      marginRight: '10px',
                    }}
                  >
                    &nbsp;Pas de données à exporter&nbsp;
                  </div>
                )}
              </ButtonGroup>
            </DivLine>
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(DutyDownloadTab);
